import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Modal,
  Popover,
  Select,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CustomDialog, {
  CLINICAL_RECORD_DIALOG_WIDTH
} from 'src/components/UI/dialog/CustomDialog';
import RecordBrowserModal from './RecordBrowserModal';
import RecordBrowser from './RecordBrowser';
import { createPortal } from 'react-dom';
import { Grid, Chip } from '@mui/material';
import TileCard from './NoteTile';
import { makeRecordOpeners } from './recordOpeners';
import { fetusSummaryRows } from '../../../pedigree-tool/pedigree/fetusEventSubject';
import { getRecordDate } from './forms/formService';
import { SIDEBAR_VALUE } from '../../sidebarValues';

import axios from 'src/axios';

import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm,
  ProcessForm,
  RiskFactorForm
} from './forms/FormsExport';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import moment from 'moment';
import { formatConsultationDates } from 'src/components/pedigree-chart/pedigree-tool/pedigree/consultationDates';
import { AppContext, ThemeContext } from 'src/store/ContextStore';
import HistoryIcon from '@mui/icons-material/History';
import { config as reproConfig } from '../../../pedigree-tool/pedigree/config';
import FilterListIcon from '@mui/icons-material/FilterList';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';

import TemplateStore from 'src/store/TemplateStore';
import AcitonDialog from 'src/components/UI/dialog/AcitonDialog';
import { formatPersonSummary } from '../../util';
import {
  recordAddressFor,
  isUnsavedMember,
  memberMatchKeyFor,
  NO_MEMBER
} from './recordAddress';
import { isAnonymousActive } from 'src/components/pedigree-chart/anonymousPedigrees';
import ModalHeader from '../diagnoses-menu/ModalHeader';

function ClinicalNotes({
  data,
  modificationHistoryHandle,
  deepCopy,
  setMemberRecords,
  isDisabled: isLocked,
  // A request from the Reproductive section to open a record scoped to one fetus (decision 21).
  // Cleared once acted on, so returning to this section later does not reopen the form.
  addFetusEvent,
  onAddFetusEventHandled,
  // TGCLOUD-1669 — (sectionKey, facts) => void, so the collapsed row can show
  // a summary of records this component already holds. Optional: the legacy
  // patient sidebar renders this component without it.
  reportSummary,
  // TGCLOUD-1669 Phase 5 — where to render the DOCKED record browser, when the
  // drawer is promoted. A portal target rather than a copy of the records:
  // the records live here, this component is mounted whatever the accordion
  // state, and a second copy in the sidebar's state would be one more thing to
  // keep in step.
  dockNode,
  // TGCLOUD-1669 — a nonce the pinned header bumps to open the record browser.
  // The browser reads `tiles`, which live here, and this component is mounted
  // whatever the accordion state, so the header can open it without the
  // records having to move or be fetched twice.
  openBrowserSignal
}) {
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { familyId } = user;
  const { theme } = React.useContext(ThemeContext);
  const anonymousActive = isAnonymousActive(content?.settings?.pedigreeSetting);

  let [isModalOpen, setOpenModal] = React.useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = React.useState(false);

  // Ignores the initial value so the browser does not spring open on mount.
  const lastBrowserSignal = useRef(openBrowserSignal);
  useEffect(() => {
    if (openBrowserSignal === lastBrowserSignal.current) return;
    lastBrowserSignal.current = openBrowserSignal;
    setIsBrowserOpen(true);
  }, [openBrowserSignal]);
  const [sortOrder, setSortOrder] = React.useState('newest');
  const browseAllRef = useRef(null);
  // TGCLOUD-1758 — the persisted record address, whether this person is saved
  // yet, and the separate key used only for in-memory matching. The reasoning
  // for keeping those three apart is in recordAddress.js.
  const familyMemberId = recordAddressFor(data);
  const memberIsUnsaved = isUnsavedMember(data);
  const memberMatchKey = memberMatchKeyFor(data);

  let [unsavedChanges, setUnsavedChanges] = React.useState({
    status: false,
    edit: false
  });
  let [noteType, setNoteType] = React.useState('Referral');
  let [isEditing, setIsEditing] = React.useState(false);
  let [currentModel, setCurrentModel] = React.useState({});
  const [staffMembers, setStaffMembers] = useState([]);

  const [filter, setFilter] = React.useState({
    Referral: true,
    Appointment: true,
    Contact: true,
    Consent: true,
    Sample: true,
    Test: true,
    Treatment: true,
    Address: true,
    'Clinical Note': true,
    'Tissue Pathology': true,
    Process: true,
    'Risk Score': true
  });

  let [tiles, setTiles] = React.useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const familyMemberIdFromURL = queryParams.get('familyMemberId');
  const appointmentIdFromURL = queryParams.get('appointmentId');
  const referralIdFromURL = queryParams.get('referralId');
  const contactIdFromURL = queryParams.get('contactId');
  const sampleIdFromURL = queryParams.get('sampleId');
  const testIdFromURL = queryParams.get('testId');
  const clinicalNoteIdFromURL = queryParams.get('clinicalNoteId');
  const consentIdFromURL = queryParams.get('consentId');
  const isOpenedModal = useRef(false);

  let [isLoading, setIsLoading] = React.useState(false);
  // TGCLOUD-1753: how many records this family holds that were withheld because
  // a clinician marked them private. Reported so the list reads as "something is
  // being held back" rather than as a gap — "withheld" and "none" are different
  // clinical statements, and a silent filter would let a reviewer read absence
  // as "no such record exists".
  const [withheldPrivate, setWithheldPrivate] = React.useState(0);

  useEffect(() => {
    const loadTemplates = async () => {
      const store = TemplateStore.getInstance();
      await store.fetchTemplates();
    };
    loadTemplates();
  }, []);

  const staffMemberOptions = React.useMemo(() => {
    return staffMembers.map((d) => ({
      label: d.firstName + ' ' + d.lastName,
      value: d.userId
    }));
  }, [staffMembers]);

  const memberTiles = React.useMemo(
    () => tiles.filter((t) => t.familyMemberId == familyMemberId),
    [tiles, familyMemberId]
  );

  const memberTests = React.useMemo(
    () => memberTiles.filter((t) => t.noteType === 'Test'),
    [memberTiles]
  );

  const memberRecordsByType = React.useMemo(() => {
    const by = {};
    for (const t of memberTiles) {
      if (!t || !t.noteType) continue;
      (by[t.noteType] = by[t.noteType] || []).push(t);
    }
    return by;
  }, [memberTiles]);

  const clinicalDataTiles = React.useMemo(() => {
    const result = [];
    (data?.diagnoses || []).forEach((d) => {
      result.push({
        _id: d._id || `disease-${d.id}`,
        noteType: 'Disease',
        familyMemberId,
        name: d.name,
        id: d.id,
        age: d.age,
        dateReported: d.dateReported,
        clinicallyConfirmed: d.clinicallyConfirmed,
        createdAt: d.dateReported || d.createdAt
      });
    });
    (data?.phenotypes || []).forEach((p) => {
      result.push({
        _id: p._id || `phenotype-${p.id}`,
        noteType: 'Phenotype',
        familyMemberId,
        name: p.name,
        id: p.id,
        dateRecorded: p.dateRecorded,
        recordedAge: p.recordedAge,
        clinicallyConfirmed: p.clinicallyConfirmed,
        createdAt: p.dateRecorded || p.createdAt
      });
    });
    (data?.genotypes || []).forEach((g) => {
      result.push({
        _id: g._id || `genotype-${g.id}`,
        noteType: 'Genotype',
        familyMemberId,
        name: g.name,
        id: g.id,
        dateRecorded: g.dateRecorded,
        recordedAge: g.recordedAge,
        clinicallyConfirmed: g.clinicallyConfirmed,
        carrierStatus: g.carrierStatus,
        createdAt: g.dateRecorded || g.createdAt
      });
    });
    return result;
  }, [data?.diagnoses, data?.phenotypes, data?.genotypes, familyMemberId]);

  // Reproductive records (Track B) — surfaced in Browse All Records alongside the clinical
  // notes so a clinician can scan all clinically relevant history in one place. Gated on the
  // repro-nodes flag; view-only here (edit via the Reproductive accordion / pedigree).
  const reproTiles = React.useMemo(() => {
    if (!reproConfig.ENABLE_REPRO_NODES) return [];
    return (data?.pregnancies || []).map((p) => ({
      _id: p._id || `pregnancy-${p.id}`,
      noteType: 'Pregnancy',
      familyMemberId,
      outcome: p.outcome,
      gestationalWeeks: p.gestationalWeeks,
      conceivedWith: p.conceivedWith,
      fetalSexKnown: p.fetalSexKnown,
      fetalSex: p.fetalSex,
      karyotype: p.karyotype,
      comments: p.comments,
      hidden: p.hidden,
      createdAt: p.createdAt,
      // The fields above mirror only the FIRST fetus (they predate the two-level model), so a multiple
      // would list one fetus and hide the rest. Empty for a single-fetus pregnancy, where those fields
      // already say everything.
      fetusRows: fetusSummaryRows(p)
    }));
  }, [data?.pregnancies, familyMemberId]);

  const user_id = user?.user_id || user?._id;
  const [riskRecords, setRiskRecords] = React.useState([]);
  const fetchRiskRecords = React.useCallback(() => {
    if (!user_id) return;
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', user_id))
      .then(({ data: res }) => setRiskRecords(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, [user_id]);
  React.useEffect(() => {
    fetchRiskRecords();
  }, [fetchRiskRecords]);

  React.useEffect(() => {
    if (!isModalOpen && noteType === 'Risk Factor') fetchRiskRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const riskTiles = React.useMemo(() => {
    const RISK_LABELS = {
      gail: 'GAIL',
      canrisk: 'CanRisk',
      prs: 'PRS',
      questionnaire: 'Questionnaire'
    };

    const memberIds = [data?.proBandId, data?.familyMemberId]
      .filter(Boolean)
      .map(String);
    return riskRecords
      .filter(
        (r) =>
          memberIds.length === 0 || memberIds.includes(String(r.familyMemberId))
      )
      .map((r) => ({
        _id: r._id,
        noteType: 'Risk Score',
        familyMemberId,
        recordType: r.recordType,
        riskLabel: RISK_LABELS[r.recordType] || r.recordType,
        gailRiskScore: r.gailRiskScore,
        canriskResult: r.canriskResult,
        prsResult: r.prsResult,
        createdAt: r.createdAt
      }));
  }, [riskRecords, familyMemberId, data?.proBandId, data?.familyMemberId]);

  // Everything the record browser lists, which is NOT the same set as the
  // CLINICAL RECORDS section: it also carries diseases, phenotypes, genotypes
  // and risk scores. One memo so the count on the header control and the rows
  // in the browser can never disagree.
  const browserTiles = React.useMemo(
    () => [...memberTiles, ...clinicalDataTiles, ...riskTiles, ...reproTiles],
    [memberTiles, clinicalDataTiles, riskTiles, reproTiles]
  );

  // TGCLOUD-1669 Phase 1 — report the collapsed-row summary upward.
  //
  // The records are fetched on mount (this component is mounted whatever its
  // accordion state), but they live in `tiles` here, where the row rendering
  // the summary cannot reach them. Nothing new is fetched; the answer is just
  // handed up.
  //
  // The count deliberately EXCLUDES diseases, phenotypes, genotypes and risk
  // scores. This section lists clinical notes; the record browser lists every
  // record type, so the same patient reads 37 here and 60 there. That is not
  // records going missing.
  const summaryDateFormat = theme?.dateFormat || 'DD/MM/YYYY';
  React.useEffect(() => {
    if (!reportSummary) return;
    if (isLoading) {
      // Not "none recorded": we do not yet know whether there are any.
      reportSummary(SIDEBAR_VALUE.clinicalNotes, { status: 'pending' });
      return;
    }
    const byType = {};
    Object.keys(memberRecordsByType).forEach((t) => {
      byType[t] = memberRecordsByType[t].length;
    });

    // Last and next are kept apart. A single "latest" that resolved to a booked
    // appointment reads as a record from the future.
    const today = moment().endOf('day');
    let last = null;
    let next = null;
    memberTiles.forEach((t) => {
      const raw = getRecordDate(t) || t.createdAt;
      if (!raw) return;
      const m = moment(raw);
      if (!m.isValid()) return;
      if (m.isAfter(today)) {
        if (!next || m.isBefore(next)) next = m;
      } else if (!last || m.isAfter(last)) {
        last = m;
      }
    });

    reportSummary(SIDEBAR_VALUE.clinicalNotes, {
      status: 'ready',
      count: memberTiles.length,
      byType,
      lastDate: last ? last.format(summaryDateFormat) : null,
      nextDate: next ? next.format(summaryDateFormat) : null,
      // What the record BROWSER would list, which includes diseases,
      // phenotypes, genotypes and risk scores and so is a larger number than
      // `count`. The header control shows this one; the section summary shows
      // `count`. They are labelled differently so neither reads as the other
      // going missing.
      browserCount: browserTiles.length
      // `withheldPrivate` is deliberately NOT reported. The X-Withheld-Private
      // header counts { familyId, private: true } — the whole FAMILY, not this
      // member (backend/routes/clinical-note.js). Putting it on a person's row
      // would tell a clinician this patient has hidden records when the hidden
      // records may belong to a sibling. The summariser supports the field, so
      // it can be switched on the day the count becomes member-scoped.
    });
  }, [
    reportSummary,
    isLoading,
    memberTiles,
    memberRecordsByType,
    browserTiles,
    summaryDateFormat
  ]);

  const patientName = `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
  const patientId = familyMemberId !== NO_MEMBER ? familyMemberId : '';

  const [isDisabled, setIsDisabled] = React.useState(false);
  const [newNote, setNewNote] = React.useState(false);

  // Open a blank record form. The RESET of `currentModel` is the load-bearing part, not a tidy-up:
  // that state survives between form opens, so without it a new record inherits whatever the previous
  // one left behind. That produced a real bug in QA — after recording a prenatal test for a fetus, the
  // next record added the ordinary way picked up the stale `pregnancyId`/`fetusId` and was labelled as
  // belonging to that fetus, i.e. a clinical misattribution on the MOTHER's own test.
  //
  // Every path that opens a NEW record goes through here so the invariant holds in one place: a new
  // record starts from a clean model unless a scope is deliberately supplied (see openScopedRecord).
  // See recordOpeners: the reset of `currentModel` is the load-bearing part, and it lives in one place
  // so the next path someone adds cannot forget it.
  const { openNewRecord, openScopedRecord } = React.useMemo(
    () =>
      makeRecordOpeners({
        setCurrentModel,
        setNoteType,
        setIsEditing,
        setNewNote,
        setIsDisabled,
        setOpenModal
      }),
    []
  );

  const addClicked = async () => {
    openNewRecord();
  };

  const [selectedRecordType, setSelectedRecordType] = useState('');

  // Honour a "record an event for this fetus" request. The scope is seeded into the model the form
  // opens with, so the new record carries `pregnancyId`/`fetusId` and is labelled as prenatal wherever
  // it is listed — rather than looking like something done to the mother.
  React.useEffect(() => {
    if (!addFetusEvent?.pregnancyId) return;
    openScopedRecord({
      type: addFetusEvent.noteType || 'Test',
      scope: {
        pregnancyId: addFetusEvent.pregnancyId,
        fetusId: addFetusEvent.fetusId
      }
    });
    onAddFetusEventHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFetusEvent]);

  const handleRecordTypeSelect = (event) => {
    const value = event.target.value;
    if (value) {
      openNewRecord({ type: value });
      setSelectedRecordType('');
    }
  };

  const handleChanges = (key) => {
    let replace = { ...filter };
    replace[key] = !replace[key];
    setFilter(replace);
  };

  // What a genetics service actually starts a record with. Deliberately a short
  // list rather than all thirteen: an empty state offering everything is a menu,
  // not a prompt. The full list stays on the Create dropdown in the section.
  const CREATABLE_FROM_EMPTY = [
    'Referral',
    'Appointment',
    'Clinical Note',
    'Sample',
    'Test'
  ];

  const [noteTypes] = React.useState([
    'Address',
    'Appointment',
    'Clinical Note',
    'Consent',
    'Contact',
    'Process',
    'Referral',
    'Sample',
    'Test',
    'Treatment',
    'Tissue Pathology',
    'Risk Factor'
  ]);

  const handleClose = () => {
    setUnsavedChanges({ status: false, edit: false });
    setOpenModal(false);
    setIsEditing(false);
    setNewNote(false);
  };

  const handleNoteClose = (event) => {
    if (event?.exit) {
      handleClose();
    } else if (unsavedChanges.edit) {
      setUnsavedChanges((prev) => ({ ...prev, status: true }));
    } else handleClose();
  };

  const handleCancel = () => {
    setUnsavedChanges((prev) => ({ ...prev, status: false }));
  };

  const isProband = !!data?.proBandId;
  const getMemberKey = React.useCallback(
    (member) => memberMatchKeyFor(member),
    []
  );

  const handleSuggestionsAccepted = React.useCallback(
    ({ phenotypes, genotypes, diagnoses }) => {
      if (!setMemberRecords || !(phenotypes || genotypes || diagnoses)) return;
      setMemberRecords((prev) => {
        let updatedMember = null;

        const updated = prev.map((member) => {
          // In-memory match, so getMemberKey's chain (pid included) is correct
          // here — see memberMatchKey. String() on both sides because a pid is a
          // number and a UPN is a string.
          const memberId = getMemberKey(member);

          if (memberId !== memberMatchKey) return member;

          updatedMember = {
            ...member,
            ...(phenotypes && { phenotypes }),
            ...(genotypes && { genotypes }),
            ...(diagnoses && { diagnoses })
          };
          return updatedMember;
        });

        return updated;
      });
    },
    [setMemberRecords, getMemberKey, memberMatchKey]
  );

  const refreshPedigreeLabels = React.useCallback(async () => {
    if (!familyId || !setMemberRecords) return;

    try {
      const response = await axios.get(
        APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)
      );
      const { proband, families = [] } = response.data || {};
      const refreshedMembers = [proband, ...families].filter(Boolean);
      const refreshedMap = new Map(
        refreshedMembers.map((member) => [getMemberKey(member), member])
      );

      const fmt = theme?.dateFormat || 'MM/DD/YYYY';

      setMemberRecords((prevRecords) =>
        prevRecords.map((member) => {
          const refreshedMember = refreshedMap.get(getMemberKey(member));
          if (!refreshedMember) return member;
          // Same shape the initial member build produces — an array, one entry
          // per consultation. This was a second copy of the map-and-join, so a
          // post-save refresh used to re-flatten what the build had just left as
          // rows and the label collapsed back to one wrapped blob until the
          // family was reloaded (TGCLOUD-1781).
          const consultationDate = formatConsultationDates(
            refreshedMember.consultationDate,
            fmt
          );
          return {
            ...member,
            consultationDate,
            confirmedDiagnosis: refreshedMember.confirmedDiagnosis || '',
            tests: refreshedMember.tests || '',
            testRecords: refreshedMember.testRecords || [],
            testResults: refreshedMember.testResults || '',
            pedigreeText: refreshedMember.pedigreeText || ''
          };
        })
      );

      dispatchUpdate({
        type: EVENTS.SET_PEDIGREE_SELECTED_USER,
        value: {
          ...users,
          selectedUser: proband,
          family: families
        }
      });
    } catch (err) {
      console.error('Failed to refresh pedigree labels:', err);
    }
  }, [dispatchUpdate, familyId, getMemberKey, setMemberRecords, theme, users]);

  const forms = (type, model = {}) => {
    switch (type) {
      case 'Referral':
        return (
          <ReferralForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Appointment':
        return (
          <AppointmentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            refreshPedigreeLabels={refreshPedigreeLabels}
          />
        );
      case 'Contact':
        return (
          <ContactForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            data={data}
            memberTests={memberTests}
            memberRecordsByType={memberRecordsByType}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Consent':
        return (
          <ConsentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Sample':
        return (
          <SampleForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Test':
        return (
          <TestForm
            handleClose={handleClose}
            data={data}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            refreshPedigreeLabels={refreshPedigreeLabels}
          />
        );
      case 'Treatment':
        return (
          <TreatmentForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Address':
        return (
          <AddressForm
            handleClose={handleClose}
            familyId={familyId}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Clinical Note':
        return (
          <ClinicalNotesForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onSuggestionsAccepted={handleSuggestionsAccepted}
          />
        );
      case 'Tissue Pathology':
        return (
          <TissuePathologyForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Process':
        return (
          <ProcessForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      case 'Risk Factor':
        return (
          <RiskFactorForm
            handleClose={handleClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
          />
        );
      default:
        return <></>;
    }
  };

  const fetchStaffMembers = () => {
    axios.get(`/clinical-notes/staff-members`).then((res) => {
      const parsedStaffMembers = res.data.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        userId: member._id
      }));
      setStaffMembers(parsedStaffMembers || []);
    });
  };

  React.useEffect(() => {
    fetchStaffMembers();
  }, []);

  const doFetch = React.useCallback(() => {
    setIsLoading(true);

    return axios
      .get(`/clinical-notes/all`, {
        params: { familyId }
      })
      .then((res) => {
        // Header, not body: this endpoint returns a bare array to several
        // consumers and is a documented API shape.
        setWithheldPrivate(Number(res.headers?.['x-withheld-private']) || 0);
        let edited = res.data;
        // Sort the notes based on the pinned property
        edited.sort(function (a) {
          if (a.pinned) return -1;
          return 1;
        });

        // Transform the data based on noteType
        const transformedData = edited.map((note) => {
          // Handle Consent type note - keep obtainedBy as string (same as Sample)
          if (note.noteType === 'Consent') {
            if (Array.isArray(note.obtainedBy) && note.obtainedBy.length > 0) {
              const s = note.obtainedBy[0];
              note.obtainedBy = `${s.firstName ?? ''} ${
                s.lastName ?? ''
              }`.trim();
            }
            // If already a string, keep it as is
          }
          if (note.noteType === 'Test') {
            note.requestedBy =
              Array.isArray(note.requestedBy) && note.requestedBy.length > 0
                ? note.requestedBy[0].userId
                : null;
          }
          if (note.noteType === 'Appointment') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;
          }
          if (note.noteType === 'Contact') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;

            note.requestChecking =
              Array.isArray(note.requestChecking) &&
              note.requestChecking.length > 0
                ? note.requestChecking[0].userId
                : null;
          }
          if (note.noteType === 'Tissue Pathology') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
            note.pathologyDoctor =
              Array.isArray(note.pathologyDoctor) &&
              note.pathologyDoctor.length > 0
                ? note.pathologyDoctor[0].userId
                : '';
          }
          if (note.noteType === 'Process') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
          }
          return note;
        });
        setTiles(transformedData);

        setIsLoading(false);
        // Auto-open a clinical record modal when a record ID is present in the URL
        if (familyMemberIdFromURL && !isOpenedModal.current) {
          const urlRecordMap = [
            { id: appointmentIdFromURL, noteType: 'Appointment' },
            { id: referralIdFromURL, noteType: 'Referral' },
            { id: contactIdFromURL, noteType: 'Contact' },
            { id: sampleIdFromURL, noteType: 'Sample' },
            { id: testIdFromURL, noteType: 'Test' },
            { id: clinicalNoteIdFromURL, noteType: 'Clinical Note' },
            { id: consentIdFromURL, noteType: 'Consent' }
          ];

          for (const { id, noteType: type } of urlRecordMap) {
            if (!id) continue;
            const record = transformedData.find(
              (data) =>
                data.familyMemberId === familyMemberIdFromURL && data._id === id
            );
            if (record) {
              setIsEditing(true);
              setCurrentModel(record);
              setNoteType(type);
              setOpenModal(true);
              isOpenedModal.current = true;
              break;
            }
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching clinical notes:', err);
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [
    familyId,
    familyMemberIdFromURL,
    appointmentIdFromURL,
    referralIdFromURL,
    contactIdFromURL,
    sampleIdFromURL,
    testIdFromURL,
    clinicalNoteIdFromURL,
    consentIdFromURL
  ]);

  React.useEffect(() => {
    // Only fetch when the modal is closed — avoids a race condition where an
    // in-flight fetch from modal-open completes *after* the post-save fetch
    // and overwrites tiles with stale (pre-save) data.
    if (isModalOpen) return;
    setTiles([]);
    doFetch();
    // TGCLOUD-1670 — the pedigree data overlays read the same records this list
    // does. Closing the record modal is the one save path that refetches HERE
    // without going through the `clinical-notes-refresh` window event, so
    // without this signal a clinician adds the contact an overlay has just told
    // them is missing and the mark stays on the node. A distinct event rather
    // than reusing `clinical-notes-refresh`, which this component listens to
    // itself and would answer with a second, redundant fetch.
    window.dispatchEvent(new CustomEvent('clinical-records-changed'));
  }, [isModalOpen, doFetch]);

  React.useEffect(() => {
    window.addEventListener('clinical-notes-refresh', doFetch);
    return () => window.removeEventListener('clinical-notes-refresh', doFetch);
  }, [doFetch]);

  const openTestRecord = React.useCallback((record) => {
    const normalized = { ...record };
    if (
      Array.isArray(normalized.requestedBy) &&
      normalized.requestedBy.length > 0
    ) {
      normalized.requestedBy = normalized.requestedBy[0].userId;
    }
    setIsEditing(true);
    setNewNote(false);
    setCurrentModel(normalized);
    setNoteType('Test');
    setIsDisabled(false);
    setOpenModal(true);
  }, []);

  const openSourceTestRecord = React.useCallback(
    async ({ testId } = {}) => {
      if (!testId) return false;
      const cached = tiles.find(
        (t) => t.noteType === 'Test' && String(t._id) === String(testId)
      );
      if (cached) {
        openTestRecord(cached);
        return true;
      }
      try {
        const { data: record } = await axios.get(
          `/clinical-notes/tests/${testId}`
        );
        if (record?._id) {
          openTestRecord(record);
          return true;
        }
      } catch (err) {
        console.error('Failed to load source test record', err);
      }
      return false;
    },
    [tiles, openTestRecord]
  );

  React.useEffect(() => {
    const handler = (e) => {
      const testId = e?.detail?.testId;
      if (!testId) return;
      openSourceTestRecord({ testId });
    };
    window.addEventListener('open-source-test-record', handler);
    return () => window.removeEventListener('open-source-test-record', handler);
  }, [openSourceTestRecord]);

  React.useEffect(() => {
    const handler = (e) => {
      const preselect = e?.detail?.model;
      setIsEditing(false);
      setCurrentModel(preselect ? { recordType: preselect } : {});
      setNoteType('Risk Factor');
      setNewNote(true);
      setIsDisabled(false);
      setOpenModal(true);
    };
    window.addEventListener('open-risk-factor-modal', handler);
    return () => window.removeEventListener('open-risk-factor-modal', handler);
  }, []);

  //Fix empty form selection values, shouldn't be necessary with radio
  React.useEffect(() => {
    if (noteType === '') setNoteType('Referral');
  }, [noteType]);

  const personSummary = React.useCallback(() => {
    let details = data;
    if (!newNote)
      details = deepCopy?.find(
        (d) => d.familyMemberId === currentModel.familyMemberId
      );
    return formatPersonSummary(details);
  }, [currentModel, newNote]);

  const renderTiles = () => {
    const MemberCards = [];
    const FamilyCards = [];
    const sorted = [...tiles, ...riskTiles].sort((a, b) => {
      const da = new Date(getRecordDate(a) || a.createdAt || 0);
      const db = new Date(getRecordDate(b) || b.createdAt || 0);
      return sortOrder === 'newest' ? db - da : da - db;
    });
    sorted.forEach((item) => {
      const tileType =
        item.noteType === 'Risk Score' && item.riskLabel
          ? 'Risk Factor'
          : item.noteType;
      const riskLabel =
        item.noteType === 'Risk Score' ? item.riskLabel : undefined;
      if (filter[item.noteType] && familyMemberId == item.familyMemberId)
        MemberCards.push(
          <TileCard
            model={item}
            key={item._id}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={deepCopy}
            staffMemberOptions={staffMemberOptions}
            carrier={data}
            onCustomClick={
              item.noteType === 'Risk Score'
                ? () => {
                    const orig = riskRecords.find((r) => r._id === item._id);
                    if (!orig) return;
                    setIsEditing(true);
                    setCurrentModel(orig);
                    setNoteType('Risk Factor');
                    setOpenModal(true);
                  }
                : undefined
            }
          />
        );

      if (item.showInFamily)
        FamilyCards.push(
          <TileCard
            model={item}
            key={item._id}
            showInFamily={item.showInFamily}
            highlightedPerson={data}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={deepCopy}
            staffMemberOptions={staffMemberOptions}
            setIsDisabled={setIsDisabled}
            carrier={data}
          />
        );
    });
    return { MemberCards, FamilyCards };
  };

  return (
    <div className="user-form">
      {/* TGCLOUD-1758 — a person who exists on the canvas but has never been
          saved has no UPN to address a record by, so record creation is not
          offered. Say why: the control simply vanishing reads as a permissions
          problem or a broken panel, and a clinician has no way to guess that
          saving the pedigree is what unlocks it. Wording matches the backend's
          400 for the same condition. */}
      {memberIsUnsaved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This person has not been saved yet. Save the pedigree to add clinical
          records for them.
        </Alert>
      )}
      {familyMemberId !== NO_MEMBER && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }} disabled={isLocked}>
            <Select
              data-va-anchor="pedigree.clinicalRecords.typePicker"
              value={selectedRecordType}
              onChange={handleRecordTypeSelect}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) => {
                if (selected === '') {
                  return (
                    <span style={{ color: '#999' }}>
                      Create New Clinical Record
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem disabled value="">
                <em>Create New Clinical Record</em>
              </MenuItem>
              {noteTypes
                // Address records hold patient PII — not creatable on an
                // anonymous pedigree (backend rejects the route too).
                .filter((item) => !(anonymousActive && item === 'Address'))
                .map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          <Tooltip title="Browse all records">
            <span>
              <IconButton
                ref={browseAllRef}
                size="small"
                onClick={() => setIsBrowserOpen(true)}
                aria-label="Browse all clinical records"
                // TGCLOUD-1669 — no longer disabled at zero records. The old
                // condition also gated on `tiles`, which is the whole FAMILY's
                // notes, so it disabled a member with no records only when no
                // relative had any either. Either way, the moment a clinician
                // most needs a way to add the first record is exactly when
                // there are none.
              >
                <ManageSearchIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={sortOrder === 'newest' ? 'Oldest first' : 'Newest first'}
          >
            <IconButton
              size="small"
              onClick={() =>
                setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))
              }
              aria-label="Toggle sort order"
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FilterClinicalRecord filter={filter} setFilter={setFilter} />
        </Box>
        {isLoading ? (
          <Chip label="Loading" />
        ) : renderTiles().MemberCards.length === 0 ? (
          <Chip
            label={
              withheldPrivate > 0
                ? 'No visible notes — private records withheld'
                : 'No Notes Found!'
            }
          />
        ) : (
          <>
            {renderTiles().MemberCards.map((tile, iter) => (
              <Grid key={iter} size={12}>
                {tile}
              </Grid>
            ))}
          </>
        )}
        {!isLoading && withheldPrivate > 0 && (
          <Grid size={12}>
            <Tooltip
              title={
                'Marked private in TrakGene Desktop. Private records are not ' +
                'shown in Cloud and cannot be opened or edited here.'
              }
            >
              <Chip
                size="small"
                variant="outlined"
                icon={<LockOutlinedIcon fontSize="small" />}
                label={`${withheldPrivate} private record${
                  withheldPrivate === 1 ? '' : 's'
                } withheld`}
                sx={{ mt: 1 }}
              />
            </Tooltip>
          </Grid>
        )}
      </Box>
      {!isLoading && (
        <Box sx={{ marginTop: 4 }}>
          <hr />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <FormLabel component="label" className="form-label">
            Family Clinical Records
          </FormLabel>
          <IconButton
            onClick={() =>
              modificationHistoryHandle(
                CONSTANTS.auditLogType['clinicalRecord'],
                filter
              )
            }
          >
            <HistoryIcon />
          </IconButton>
        </Box>

        {renderTiles().FamilyCards.map((tile, iter) => (
          <Grid key={iter} size={12}>
            {tile}
          </Grid>
        ))}

        {renderTiles().FamilyCards.length === 0 && (
          <Chip label={`No Notes Found!`} />
        )}
      </Box>
      <AcitonDialog
        open={unsavedChanges.status}
        title={'Unsaved Changes'}
        saveText="Discard changes"
        handleClose={handleCancel}
        onSubmit={handleClose}
      >
        You have unsaved changes that will be lost if you continue. Would you
        like to discard your changes and proceed anyway?
      </AcitonDialog>
      <CustomDialog
        maxWidth={
          noteType === 'Risk Factor' ? 'lg' : CLINICAL_RECORD_DIALOG_WIDTH
        }
        title={
          <ModalHeader
            title={isEditing ? `Edit ${noteType}` : `Create New ${noteType}`}
            personSummary={personSummary}
          />
        }
        allowBackgroundClose={true}
        open={isModalOpen}
        handleClose={handleNoteClose}
        minHeight="auto"
        onClose={true}
        footerAction={
          isEditing && currentModel?._id && modificationHistoryHandle ? (
            <IconButton
              size="small"
              title="Record history"
              onClick={() =>
                modificationHistoryHandle(
                  CONSTANTS.auditLogType.clinicalRecord,
                  {},
                  currentModel._id
                )
              }
            >
              <HistoryIcon />
            </IconButton>
          ) : null
        }
      >
        <div data-va-anchor="pedigree.clinicalRecords.dialog">
          {forms(noteType, currentModel)}
        </div>
      </CustomDialog>
      {dockNode &&
        createPortal(
          <RecordBrowser
            tiles={browserTiles}
            isLoading={isLoading}
            staffMemberOptions={staffMemberOptions}
            carrier={data}
            facetLayout="strip"
            // Only ONE browser binds the window key handling at a time. With
            // the dialog open over the top, the arrow keys belong to it.
            active={!isBrowserOpen}
            onCreateRecord={(type) => openNewRecord({ type })}
            creatableTypes={CREATABLE_FROM_EMPTY.filter(
              (t) => !(anonymousActive && t === 'Address')
            )}
          />,
          dockNode
        )}

      <RecordBrowserModal
        open={isBrowserOpen}
        onClose={() => {
          setIsBrowserOpen(false);
          browseAllRef.current?.focus();
        }}
        tiles={browserTiles}
        onCreateRecord={(type) => {
          setIsBrowserOpen(false);
          openNewRecord({ type });
        }}
        creatableTypes={CREATABLE_FROM_EMPTY.filter(
          (t) => !(anonymousActive && t === 'Address')
        )}
        isLoading={isLoading}
        patientName={patientName}
        patientId={patientId}
        patientDob={data?.dob}
        staffMemberOptions={staffMemberOptions}
        carrier={data}
      />
    </div>
  );
}

const FilterClinicalRecord = ({ filter, setFilter }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const allOptions = Object.keys(filter).sort((a, b) => a.localeCompare(b));
  const selectedValues = allOptions.filter((key) => filter[key]);
  const selectedText =
    selectedValues.length > 0 && selectedValues.length < allOptions.length
      ? `${selectedValues.length} selected`
      : '';

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (name) => {
    setFilter((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSelectAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = true;
    });
    setFilter(newFilter);
  };

  const handleClearAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = false;
    });
    setFilter(newFilter);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label="Filter clinical records"
        >
          <FilterListIcon />
        </IconButton>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {selectedText}
        </Box>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { p: 1, minWidth: 260 } }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Button onClick={handleSelectAll} size="small">
            Select All
          </Button>
          <Button onClick={handleClearAll} size="small">
            Clear All
          </Button>
        </Box>
        <MenuList disablePadding>
          {allOptions.map((name) => (
            <MenuItem
              key={name}
              onClick={() => handleToggle(name)}
              sx={{ py: 0.5 }}
            >
              <Checkbox checked={filter[name]} size="small" />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </div>
  );
};

export default ClinicalNotes;
